/* ============================================================================
 * dbe-ai-label.js — EU AI Act Art. 50 disclosure inside the PhotoSwipe lightbox
 * ----------------------------------------------------------------------------
 * PhotoSwipe renders into a global .pswp container (layout/theme.liquid), which
 * lives outside the product gallery DOM. Gallery items are built in theme.js
 * purely from data-zoom-src / data-width / data-height, with captionEl: false.
 * A Liquid-rendered badge inside .product-media-container therefore disappears
 * the moment an image is enlarged — precisely the full-screen, high-resolution
 * view in which an AI image looks most authentic.
 *
 * This file re-applies the disclosure there WITHOUT touching theme.js, which is
 * Shopify-synced and silently overwritten by admin commits.
 *
 * Approach:
 *  - Collect the zoom URLs of gallery slides that carry a .dbe-ai-label.
 *  - Clone an existing label so the merchant's text/style settings are inherited
 *    with no duplication of theme settings into JS.
 *  - Attach the clone to the .pswp ROOT (not .pswp__ui), so PhotoSwipe's idle
 *    timeout — timeToIdle: 4000 -> hideControls() — cannot hide a legal notice.
 *  - Identify the visible slide geometrically (the .pswp__img spanning the
 *    viewport centre) rather than by PhotoSwipe internals, so this keeps working
 *    across PhotoSwipe versions and swipe/arrow/zoom navigation.
 * ========================================================================== */

(function () {
  'use strict';

  var POLL_MS = 120;
  var aiZoomSrcs = Object.create(null);
  var badge = null;
  var pollTimer = null;

  function normalise(src) {
    if (!src) return '';
    // Compare protocol-relative and query-stripped forms so a CDN cache-busting
    // param can never silently drop the disclosure.
    return String(src).replace(/^https?:/, '').split('?')[0];
  }

  function remember(src) {
    if (!src) return;
    aiZoomSrcs[src] = true;
    aiZoomSrcs[normalise(src)] = true;
  }

  /* Rebuild the AI zoom-URL set from the current DOM. Cheap, and re-run on
   * section load and on every lightbox open so variant switches, quickview and
   * ajax-injected galleries are picked up. */
  function collect() {
    aiZoomSrcs = Object.create(null);

    var containers = document.querySelectorAll('.product-media-container');
    for (var i = 0; i < containers.length; i++) {
      var container = containers[i];
      if (!container.querySelector('.dbe-ai-label')) continue;

      var zoomable = container.querySelectorAll('[data-zoom-src]');
      for (var j = 0; j < zoomable.length; j++) {
        remember(zoomable[j].getAttribute('data-zoom-src'));
      }
    }
  }

  /* Clone an in-page label so text and style follow the theme settings. */
  function ensureBadge(pswp) {
    if (badge && pswp.contains(badge)) return badge;

    var source = document.querySelector('.dbe-ai-label');
    if (!source) return null;

    badge = source.cloneNode(true);
    badge.className = 'dbe-ai-label dbe-ai-label--lightbox';
    // Preserve the merchant's chosen style variant, drop in-page placement.
    if (source.classList.contains('dbe-ai-label--subtle')) {
      badge.classList.add('dbe-ai-label--subtle');
    } else {
      badge.classList.add('dbe-ai-label--solid');
    }
    badge.setAttribute('data-dbe-ai-visible', 'false');
    pswp.appendChild(badge);
    return badge;
  }

  /* The visible slide is the .pswp__img spanning the horizontal viewport centre.
   * Geometry beats introspecting PhotoSwipe's recycled 3-item carousel. */
  function visibleImage(pswp) {
    var imgs = pswp.querySelectorAll('.pswp__img');
    var centre = window.innerWidth / 2;

    for (var i = 0; i < imgs.length; i++) {
      var rect = imgs[i].getBoundingClientRect();
      if (rect.width > 0 && rect.left <= centre && rect.right >= centre) {
        return imgs[i];
      }
    }
    return null;
  }

  function update(pswp) {
    if (!badge) return;

    var img = visibleImage(pswp);
    var src = img ? img.getAttribute('src') || img.currentSrc : '';
    var isAi = !!(src && (aiZoomSrcs[src] || aiZoomSrcs[normalise(src)]));

    badge.setAttribute('data-dbe-ai-visible', isAi ? 'true' : 'false');
  }

  function stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
    if (badge) badge.setAttribute('data-dbe-ai-visible', 'false');
  }

  function startPolling(pswp) {
    if (pollTimer) return;

    pollTimer = setInterval(function () {
      // Self-terminating: stop if the lightbox closed or the node went away, so
      // the timer can never outlive the DOM it observes.
      if (!pswp.isConnected || !pswp.classList.contains('pswp--open')) {
        stopPolling();
        return;
      }
      update(pswp);
    }, POLL_MS);

    update(pswp);
  }

  function watch() {
    var pswp = document.querySelector('.pswp');
    if (!pswp || pswp.dataset.dbeAiInit === 'true') return;
    pswp.dataset.dbeAiInit = 'true';

    var observer = new MutationObserver(function () {
      if (pswp.classList.contains('pswp--open')) {
        collect();
        if (ensureBadge(pswp)) startPolling(pswp);
      } else {
        stopPolling();
      }
    });

    observer.observe(pswp, { attributes: true, attributeFilter: ['class'] });
  }

  function init() {
    collect();
    watch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: sections re-render without a page load.
  document.addEventListener('shopify:section:load', init);
})();
